/**
 * The Fleet Editor's route table (ADR-111 IA-6), in one place so App.tsx and the
 * shell-level contract tests render the SAME routes. A test that mirrored these
 * by hand could pass over a production table that no longer matched it.
 *
 * All the agent-centric paths render the one `FleetEditor` component; the shape
 * of the path is what tells it which agent, facet, or agentless area to show.
 */
import { Navigate, Route } from 'react-router-dom'
import { FleetEditor, LegacyWorkflowRedirect } from './FleetEditor'
import { NEW_AGENT_PATH } from './fleetEditorArea'
import { AuthoringRuns } from './AuthoringRuns'
import { AuthoringRunRedirect } from './AuthoringRunRedirect'

export const fleetEditorRoutes = [
  <Route key="fe-root" path="/fleet-editor" element={<FleetEditor />} />,
  // ADR-177 — the run tree lives on the WORKFLOW PAGE, in the Runs tab, because that is
  // where the context already is. A run reached from a route of its own left the operator
  // somewhere with no way back to what they were editing.
  //
  // These two routes remain, and they do different jobs:
  //
  // Under /fleet-editor rather than /workflows because /workflows/* is a permanent
  // redirect to /fleet-editor — a run route there would be swallowed before it rendered.
  //
  // Both outrank `fe-legacy` (/fleet-editor/:agent/:name) without depending on position:
  // react-router scores a static segment above a dynamic one, so `runs` wins over
  // `:agent`.
  // The FLEET-WIDE index stays. Per-workflow history now lives on the workflow page, but
  // "which stage refuses most across every workflow, and did that change when we shipped
  // X" is ADR-175's question and is inherently cross-workflow — a per-workflow panel
  // cannot answer it.
  <Route key="fe-runs" path="/fleet-editor/runs" element={<AuthoringRuns />} />,
  // ADR-177 — a run's own page became a SECTION of the workflow editor, where the context
  // already is. This route stays as the shareable address: it resolves the run to its slot
  // and sends the browser to that workflow with the run open. A run URL is how one person
  // hands another the evidence, and a panel has no address.
  <Route key="fe-run" path="/fleet-editor/runs/:id" element={<AuthoringRunRedirect />} />,
  // Creating an agent is an AREA with its own URL, not a panel stacked over
  // whichever agent happened to be selected. It outranks the legacy
  // /fleet-editor/:agent/:name route below because react-router scores a static
  // segment above a dynamic one, so nothing depends on this line's position.
  <Route key="fe-new" path={NEW_AGENT_PATH} element={<FleetEditor />} />,
  // Platform skills are a fleet-wide, read-only area. Its detail URL owns the
  // skill name while remaining outside the agent-scoped facet namespace.
  <Route key="fe-platform-skills" path="/fleet-editor/platform-skills" element={<FleetEditor />} />,
  <Route key="fe-platform-skill" path="/fleet-editor/platform-skills/:name" element={<FleetEditor />} />,
  <Route key="fe-facet" path="/fleet-editor/agents/:agent/:facet" element={<FleetEditor />} />,
  // The ENTITY deep link: an agent's facet focused on one thing inside it —
  // .../workflows/ticket-triage, .../crons/daily-digest. Operators share these
  // by copying the address bar, so every facet that focuses one entity must put
  // that entity in the URL and resolve it back on paste.
  //
  // Generic in `:facet` rather than one route per facet, because the previous
  // shape (a hardcoded `workflows/:name`) is what made Crons a special case with
  // no deep link at all: adding one meant adding a route, and nobody did. Now a
  // facet earns its deep link by reading `:name`, not by editing this table.
  // Four segments, so it cannot shadow NEW_AGENT_PATH (three) or the facet route
  // above; react-router scores the static `agents` prefix ahead of nothing else
  // here, and a facet that ignores `:name` renders exactly as it does today.
  <Route key="fe-entity" path="/fleet-editor/agents/:agent/:facet/:name" element={<FleetEditor />} />,
  <Route key="fe-resources" path="/fleet-editor/resources" element={<FleetEditor />} />,
  // ADR-127 D1: the Data area is a FLEET-level peer of Resources, not an agent
  // facet — a set is referenced by many agents, so hanging it off one would
  // make every other agent's use of it invisible.
  <Route key="fe-data" path="/fleet-editor/data" element={<FleetEditor />} />,
  // A data set is a shareable entity too (ADR-111 IA-6). Its own route rather
  // than the agent-scoped one above, because ADR-127 D1 makes Data a FLEET-level
  // peer of Resources — a set is referenced by many agents, so hanging it off one
  // would make every other agent's use of it invisible. `*` because a set ref
  // carries a version and may carry the reserved `org/` namespace
  // (`org/asana-sections@v1`), so the name is not a single path segment.
  <Route key="fe-dataset" path="/fleet-editor/data/*" element={<FleetEditor />} />,
  <Route key="fe-files" path="/fleet-editor/files" element={<Navigate to="/fleet-editor/resources" replace />} />,
  <Route key="fe-legacy" path="/fleet-editor/:agent/:name" element={<LegacyWorkflowRedirect />} />,
]
